import * as React from 'react';
import { FormikValues } from 'formik';
import Done from '@mui/icons-material/Done';
import Close from '@mui/icons-material/Close';
import Download from '@mui/icons-material/Download';
import { Chip, Typography, IconButton, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';

import { Option } from 'lib/types';
import { SettingDisplay } from 'lib/SettingDisplay';
import { MuiCardForm } from 'lib/form/MuiCardForm';
import { SettingDisplaySwitch } from '../SettingDisplaySwitch';
import { useDownloadAsset } from 'lib/hooks/useDownloadAsset';
import { ImageFormField } from 'lib/form/fields/ImageFormField';
import { NumberFormField } from 'lib/form/fields/NumberFormField';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { EagleEyeGiftCardSettingsEdit } from '../GiftCardProvider/EagleEye/EagleEyeGiftCardSettingsEdit';
import { EagleEyeGiftCardSettingsDisplay } from 'components/settings/GiftCardProvider/EagleEye/EagleEyeGiftCardSettingsDisplay';
import { PowercardSettingsDisplay } from '../LoyaltyProvider/Powercard/PowercardSettingsDisplay';
import { PowercardSettingsEdit } from '../LoyaltyProvider/Powercard/PowercardSettingsEdit';
import { PowercardGiftCardSettingsEdit } from '../GiftCardProvider/Powercard/PowercardGiftCardSettingsEdit';
import { PowercardGiftCardSettingsDisplay } from 'components/settings/GiftCardProvider/Powercard/PowercardGiftCardSettingsDisplay';
import { RevelGiftCardSettingsEdit } from '../GiftCardProvider/Revel/RevelGiftCardSettingsEdit';
import { RevelGiftCardSettingsDisplay } from 'components/settings/GiftCardProvider/Revel/RevelGiftCardSettingsDisplay';
import { ArteemoSettingsEdit } from '../LoyaltyProvider/Arteemo/ArteemoSettingsEdit';
import { ArteemoSettingsDisplay } from '../LoyaltyProvider/Arteemo/ArteemoSettingsDisplay';
import { ToggleGiftCardSettingsEdit } from '../GiftCardProvider/Toggle/ToggleGiftCardSettingsEdit';
import { ToggleGiftCardSettingsDisplay } from 'components/settings/GiftCardProvider/Toggle/ToggleGiftCardSettingsDisplay';
import { DEFAULT_MAX_USER_METRICS } from 'components/metrics/model/loyalty-tier';

const giftCardProviders: Option[] = [
    { label: 'None', value: 'NONE' },
    { label: 'Powercard', value: 'POWERCARD' },
    { label: 'Square', value: 'SQUARE' },
    { label: 'Toggle', value: 'TOGGLE' },
    { label: 'Revel', value: 'REVEL' },
    { label: 'Eagle Eye', value: 'EAGLE_EYE' }
];

enum AwardsScreenBarcodePosition {
    INLINE = 'INLINE',
    TOOLBAR_BUTTON = 'TOOLBAR_BUTTON',
    HIDDEN = 'HIDDEN'
}

const awardsScreenBarcodePositionOptions: Option[] = [
    { label: 'Unset', value: null },
    { label: 'Inline on Screen', value: AwardsScreenBarcodePosition.INLINE },
    { label: 'Button in Toolbar', value: AwardsScreenBarcodePosition.TOOLBAR_BUTTON },
    { label: 'Not Shown', value: AwardsScreenBarcodePosition.HIDDEN }
];

const couponProviders: Option[] = [
    { label: 'None', value: null },
    { label: 'Eagle Eye', value: 'EAGLE_EYE' }
];

const userDeleteActionAvailableProviders = ['POWERCARD', 'ATREEMO', 'REVEL'];

const loyaltyProviders: Option[] = [
    { label: 'Pepper', value: 'PEPPER' },
    { label: 'Powercard', value: 'POWERCARD' },
    { label: 'Atreemo', value: 'ATREEMO' },
    { label: 'Revel', value: 'REVEL' },
    { label: 'Comtrex', value: 'COMTREX' }
];

const squareLoyaltyProviders: Option[] = [
    { label: 'Pepper', value: 'PEPPER' },
    { label: 'Square', value: 'SQUARE' }
];

const getUserDeletionSettingLabel = (
    loyaltyProvider: (typeof userDeleteActionAvailableProviders)[number]
) => {
    if (userDeleteActionAvailableProviders.includes(loyaltyProvider)) {
        const provider = loyaltyProviders.find(provider => provider.value === loyaltyProvider).label;

        return `Delete User In ${provider}`;
    }
    return '';
};

const PREFIX = 'MarketingSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`,
    image: `${PREFIX}-image`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2),
        overflowY: 'visible' // Needed for the dropdowns
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    },
    [`& .${classes.image}`]: {
        width: 'auto',
        height: 'auto',
        maxHeight: '400px',
        maxWidth: '100%'
    }
}));

type IProps = {
    settings: IPublicAndPrivateSettings;
    locationId?: string;
    isSquarePos: boolean;
    internalSettingsEditable: boolean;
    internalSettingsVisible: boolean;
    tenantId: string;
    globalLoyaltyProvider: string;
    globalGiftCardProvider: string;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

export function LoyaltySettingsCard({
    settings,
    tenantId,
    isSquarePos,
    locationId,
    internalSettingsVisible,
    internalSettingsEditable,
    globalLoyaltyProvider,
    globalGiftCardProvider,
    onSubmit
}: IProps) {
    const computedLoyaltyProviders = React.useMemo(
        () => (isSquarePos ? squareLoyaltyProviders : loyaltyProviders),
        [isSquarePos]
    );

    const awardsPlaceHolderUrl = `${process.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/awards_placeholder`;
    const { loading: awardsPlaceholderLoading, handleDownload: handleDownloadAwardsPlaceholder } =
        useDownloadAsset({
            src: awardsPlaceHolderUrl,
            fileName: 'awards_placeholder'
        });

    const externalAwardsPlaceHolderUrl = `${process.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/external_awards_placeholder`;
    const {
        loading: externalAwardsPlaceholderLoading,
        handleDownload: handleDownloadExternalAwardsPlaceholder
    } = useDownloadAsset({
        src: externalAwardsPlaceHolderUrl,
        fileName: 'external_awards_placeholder'
    });

    const awardExpiryIconUrl = `${process.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/award_expiry_icon`;
    const { loading: awardExpiryIconLoading, handleDownload: handleDownloadAwardExpiryIcon } =
        useDownloadAsset({
            src: awardExpiryIconUrl,
            fileName: 'award_expiry_icon'
        });

    const noAwardsPlaceholderUrl = `${process.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/no_awards_placeholder`;
    const { loading: noAwardsPlaceholderLoading, handleDownload: handleDownloadNoAwardsPlaceholder } =
        useDownloadAsset({
            src: noAwardsPlaceholderUrl,
            fileName: 'no_awards_placeholder'
        });

    const renderLoyaltyEdit = React.useCallback(
        (values: FormikValues) => (
            <Box display="flex" flexDirection="column">
                {locationId === undefined && (
                    <>
                        <SettingDisplay label="Loyalty Enabled">
                            <SwitchFormField name="loyaltyEnabled" />
                        </SettingDisplay>
                        <SettingDisplay label="Allow Multiple Award Selection on Checkout">
                            <SwitchFormField name="loyaltyMultipleAwardRedemptionsEnabled" />
                        </SettingDisplay>
                        {internalSettingsEditable && (
                            <SettingDisplay label="Premium Loyalty Enabled">
                                <SwitchFormField name="premiumLoyaltyEnabled" />
                            </SettingDisplay>
                        )}
                        {internalSettingsEditable && (
                            <SettingDisplay label="Number of User Metrics Enabled">
                                <NumberFormField name="maximumNumberOfUserMetrics" />
                            </SettingDisplay>
                        )}
                        {internalSettingsEditable && (
                            <SettingDisplay label="Scheduled Notifications Enabled">
                                <SwitchFormField name="scheduledNotificationsEnabled" />
                            </SettingDisplay>
                        )}
                        {internalSettingsEditable && (
                            <SettingDisplay label="Triggered Notifications Enabled">
                                <SwitchFormField name="triggeredNotificationsEnabled" />
                            </SettingDisplay>
                        )}
                        <SettingDisplay label="Loyalty Provider">
                            <SelectFormField name="loyaltyProvider" options={computedLoyaltyProviders} />
                        </SettingDisplay>
                        {userDeleteActionAvailableProviders.includes(values.loyaltyProvider) && (
                            <SettingDisplay label={getUserDeletionSettingLabel(values.loyaltyProvider)}>
                                <SwitchFormField name="userDeletion.deleteLoyaltyAccountOnUserDeletion" />
                            </SettingDisplay>
                        )}
                        <SettingDisplay
                            label="Show Balance Awards"
                            description="Should awards be added in API /awards for loyalty provider balances"
                        >
                            <SwitchFormField name="loyaltyShowBalanceAward" />
                        </SettingDisplay>
                        <SettingDisplay label="Recalculate Service Charge On Discount">
                            <SwitchFormField name="loyaltyRecalculateServiceChargeOnDiscount" />
                        </SettingDisplay>
                    </>
                )}
                {values.loyaltyProvider === 'POWERCARD' && <PowercardSettingsEdit locationId={locationId} />}
                {values.loyaltyProvider === 'ATREEMO' && <ArteemoSettingsEdit locationId={locationId} />}
                {locationId === undefined && (
                    <>
                        <SettingDisplay label="Gift Card Enabled">
                            <SwitchFormField name="giftCardEnabled" />
                        </SettingDisplay>
                        <SettingDisplay label="Gift Card Provider">
                            <SelectFormField name="giftCardProvider" options={giftCardProviders} />
                        </SettingDisplay>
                        <SettingDisplay label="Tipping Included">
                            <SwitchFormField name="giftCardTippingIncluded" />
                        </SettingDisplay>
                    </>
                )}
                {values.giftCardProvider === 'EAGLE_EYE' && (
                    <EagleEyeGiftCardSettingsEdit locationId={locationId} />
                )}
                {values.giftCardProvider === 'POWERCARD' && (
                    <PowercardGiftCardSettingsEdit settings={settings} locationId={locationId} />
                )}
                {locationId !== undefined && (
                    <SettingDisplay label="Coupon Provider">
                        <SelectFormField
                            name="couponProvider"
                            options={couponProviders}
                            displayEmpty={true}
                            emptyLabel="None"
                        />
                    </SettingDisplay>
                )}
                {values.giftCardProvider === 'TOGGLE' && (
                    <ToggleGiftCardSettingsEdit settings={settings} locationId={locationId} />
                )}
                {values.giftCardProvider === 'REVEL' && (
                    <RevelGiftCardSettingsEdit settings={settings} locationId={locationId} />
                )}
                {locationId === undefined && (
                    <>
                        <SettingDisplay label="Solo Stamps Enabled">
                            <SwitchFormField name="soloStampsEnabled" />
                        </SettingDisplay>
                        <SettingDisplay label="SnowShoe API Key">
                            <TextFormField name="snowshoeKey" />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Awards Placeholder"
                            buildtimeAndroid
                            buildtimeIOS
                            description="600px by 400px PNG or JPEG"
                        >
                            <ImageFormField
                                name="awardsPlaceholder"
                                aspect={600 / 400}
                                fileName="awards_placeholder"
                                uploadFolder="app_assets"
                                unremovable
                            />
                        </SettingDisplay>
                        <SettingDisplay
                            label="External Awards Placeholder"
                            buildtimeAndroid
                            buildtimeIOS
                            description="600px by 400px PNG or JPEG"
                        >
                            <ImageFormField
                                name="externalAwardsPlaceholder"
                                aspect={600 / 400}
                                fileName="external_awards_placeholder"
                                uploadFolder="app_assets"
                                unremovable
                            />
                        </SettingDisplay>
                        <SettingDisplay
                            label="Expiry Icon on Award"
                            buildtimeAndroid
                            buildtimeIOS
                            description="50px by 50px PNG or JPEG"
                        >
                            <ImageFormField
                                name="awardExpiryIcon"
                                aspect={1}
                                fileName="award_expiry_icon"
                                uploadFolder="app_assets"
                                unremovable
                            />
                        </SettingDisplay>
                        <SettingDisplay label="Coupon Provider">
                            <SelectFormField
                                name="couponProvider"
                                options={couponProviders}
                                displayEmpty={true}
                                emptyLabel="None"
                            />
                        </SettingDisplay>
                        {values.couponProvider === 'EAGLE_EYE' && (
                            <>
                                <SettingDisplay label="Production Environment">
                                    <SwitchFormField name="eagleEyeIsProduction" />
                                </SettingDisplay>
                                <SettingDisplay label="Client ID">
                                    <TextFormField name="eagleEyeClientId" />
                                </SettingDisplay>
                                <SettingDisplay label="Secret">
                                    <TextFormField name="eagleEyeSecret" />
                                </SettingDisplay>
                            </>
                        )}
                        <SettingDisplay
                            label="No Awards Placeholder Image"
                            description="600px by 400px PNG or JPEG"
                        >
                            <ImageFormField
                                name="noAwardsPlaceholder"
                                aspect={600 / 400}
                                fileName="no_awards_placeholder"
                                uploadFolder="app_assets"
                                unremovable
                            />
                        </SettingDisplay>
                        <SettingDisplay label="Barcode Position on Awards Screen">
                            <SelectFormField
                                name="awardsScreenBarcodePosition"
                                options={awardsScreenBarcodePositionOptions}
                                displayEmpty={true}
                                emptyLabel="Unset"
                            />
                        </SettingDisplay>
                    </>
                )}
            </Box>
        ),
        [locationId, internalSettingsEditable, computedLoyaltyProviders, settings]
    );

    return (
        <StyledMuiCardForm
            title="Loyalty"
            childrenWhileEditing={renderLoyaltyEdit}
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
            initialValues={{
                loyaltyEnabled: settings?.loyaltyEnabled ?? false,
                loyaltyMultipleAwardRedemptionsEnabled:
                    settings?.loyalty?.multipleAwardRedemptionsEnabled ?? false,
                premiumLoyaltyEnabled: settings?.premiumLoyaltyEnabled ?? false,
                maximumNumberOfUserMetrics:
                    settings?.premiumLoyalty?.maximumNumberOfUserMetrics ?? DEFAULT_MAX_USER_METRICS,
                scheduledNotificationsEnabled: settings?.scheduledNotificationsEnabled ?? false,
                triggeredNotificationsEnabled: settings?.triggeredNotificationsEnabled ?? false,
                loyaltyProvider: globalLoyaltyProvider,
                giftCardEnabled: settings?.giftCardPaymentEnabled ?? false,
                giftCardProvider: globalGiftCardProvider,
                soloStampsEnabled: settings?.physicalStampsEnabled ?? false,
                snowshoeKey: settings?.snowShoe?.key,
                awardsPlaceholder: awardsPlaceHolderUrl,
                // eslint-disable-next-line max-len
                externalAwardsPlaceholder: externalAwardsPlaceHolderUrl,
                awardExpiryIcon: awardExpiryIconUrl,
                couponProvider: settings?.couponProvider,
                eagleEyeIsProduction: settings?.eagleEye?.isProduction ?? false,
                eagleEyeClientId: settings?.eagleEye?.clientId,
                eagleEyeSecret: settings?.eagleEye?.secret,
                eagleEyeGiftCardFullRedemptionOnlyCampaignIds:
                    settings?.eagleEye?.fullRedemptionOnlyCampaignIds ?? [],
                noAwardsPlaceholder: noAwardsPlaceholderUrl,
                awardsScreenBarcodePosition: settings?.app?.awardsScreenBarcodePosition,
                powercardIsProduction: settings?.powercardLoyalty?.isProduction ?? false,
                powercardApiToken: settings?.powercardLoyalty?.apiToken,
                powercardMerchantId: settings?.powercardLoyalty?.m_id,
                powercardLocationId: settings?.powercardLoyalty?.l_id,
                giftCardTippingIncluded: settings?.giftCardPayments?.tippingIncluded ?? false,
                powercardGiftCardIsProduction: settings?.powercardGift?.isProduction ?? false,
                powercardGiftCardApiToken: settings?.powercardGift?.apiToken,
                powercardGiftCardMerchantId: settings?.powercardGift?.m_id,
                powercardGiftCardGiftPID: settings?.powercardGift?.gift_pid,
                powercardGiftCardLocationId: settings?.powercardGift?.l_id,
                toggleGiftCardUsername: settings?.toggle?.username,
                toggleGiftCardPassword: settings?.toggle?.password,
                toggleGiftCardEposReference: settings?.toggle?.eposReference,
                revelGiftCardUserId: settings?.revel?.userId,
                loyaltyShowBalanceAward: !!settings?.loyalty?.showBalanceAward,
                loyaltyDisplayExternalMemberNumber: !!settings?.loyalty?.displayExternalMemberNumber,
                loyaltyRecalculateServiceChargeOnDiscount:
                    !!settings?.loyalty?.recalculateServiceChargeOnDiscount,
                atreemoHost: settings?.atreemo?.host,
                atreemoUsername: settings?.atreemo?.username,
                atreemoPassword: settings?.atreemo?.password,
                atreemoLocationId: settings?.atreemo?.locationId,
                atreemoVoucherCategoryId: settings?.atreemo?.loyaltyBalanceVoucherCategoryId,
                atreemoVoucherRedemptionValue: settings?.atreemo?.loyaltyBalanceRedemptionValue,
                userDeletion: {
                    ...settings?.userDeletion,
                    deleteLoyaltyAccountOnUserDeletion:
                        settings?.userDeletion?.deleteLoyaltyAccountOnUserDeletion === undefined
                            ? true
                            : settings?.userDeletion?.deleteLoyaltyAccountOnUserDeletion
                }
            }}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    {locationId === undefined && (
                        <>
                            <SettingDisplay label="Loyalty Enabled">
                                {settings?.loyaltyEnabled && <Done />}
                                {!settings?.loyaltyEnabled && <Close />}
                            </SettingDisplay>
                            <SettingDisplay label="Allow Multiple Award Selection on Checkout">
                                {settings?.loyalty?.multipleAwardRedemptionsEnabled && <Done />}
                                {!settings?.loyalty?.multipleAwardRedemptionsEnabled && <Close />}
                            </SettingDisplay>
                            {internalSettingsVisible && (
                                <SettingDisplaySwitch
                                    title="Premium Loyalty Enabled"
                                    checked={settings?.premiumLoyaltyEnabled ?? false}
                                />
                            )}
                            {internalSettingsVisible && (
                                <SettingDisplay label="Number of User Metrics Enabled">
                                    <Typography variant="body2">
                                        {settings?.premiumLoyalty?.maximumNumberOfUserMetrics ??
                                            DEFAULT_MAX_USER_METRICS}
                                    </Typography>
                                </SettingDisplay>
                            )}
                            {internalSettingsVisible && (
                                <SettingDisplaySwitch
                                    title="Scheduled Notifications Enabled"
                                    checked={settings?.scheduledNotificationsEnabled ?? false}
                                />
                            )}
                            {internalSettingsVisible && (
                                <SettingDisplaySwitch
                                    title="Triggered Notifications Enabled"
                                    checked={settings?.triggeredNotificationsEnabled ?? false}
                                />
                            )}
                            <SettingDisplay label="Loyalty Provider">
                                <Chip
                                    label={
                                        computedLoyaltyProviders.find(
                                            option => option.value === settings?.loyaltyProvider
                                        )?.label ?? 'None'
                                    }
                                    color="primary"
                                />
                            </SettingDisplay>
                            {userDeleteActionAvailableProviders.includes(globalLoyaltyProvider) && (
                                <SettingDisplaySwitch
                                    title={getUserDeletionSettingLabel(globalLoyaltyProvider)}
                                    checked={
                                        settings?.userDeletion?.deleteLoyaltyAccountOnUserDeletion ?? true
                                    }
                                />
                            )}
                            <SettingDisplaySwitch
                                title="Show Balance Awards"
                                description="Should awards be added in API /awards for loyalty provider balances"
                                checked={settings?.loyalty?.showBalanceAward ?? false}
                            />
                            <SettingDisplaySwitch
                                title="Recalculate Service Charge On Discount"
                                checked={settings?.loyalty?.recalculateServiceChargeOnDiscount ?? false}
                            />
                        </>
                    )}
                    {globalLoyaltyProvider === 'POWERCARD' && (
                        <PowercardSettingsDisplay settings={settings} locationId={locationId} />
                    )}
                    {globalLoyaltyProvider === 'ATREEMO' && (
                        <ArteemoSettingsDisplay settings={settings} locationId={locationId} />
                    )}
                    {locationId === undefined && (
                        <>
                            <SettingDisplaySwitch
                                title="Gift Card Enabled"
                                checked={settings?.giftCardPaymentEnabled}
                            />
                            <SettingDisplay label="Gift Card Provider">
                                <Chip
                                    label={
                                        giftCardProviders.find(
                                            option => option.value === settings?.giftCardPaymentProvider
                                        )?.label ?? 'None'
                                    }
                                    color="primary"
                                />
                            </SettingDisplay>
                            <SettingDisplaySwitch
                                title="Tipping Included"
                                checked={settings.giftCardPayments?.tippingIncluded ?? false}
                            />
                        </>
                    )}
                    {globalGiftCardProvider === 'EAGLE_EYE' && (
                        <EagleEyeGiftCardSettingsDisplay settings={settings} locationId={locationId} />
                    )}
                    {globalGiftCardProvider === 'POWERCARD' && (
                        <PowercardGiftCardSettingsDisplay settings={settings} locationId={locationId} />
                    )}
                    {locationId !== undefined && (
                        <SettingDisplay label="Coupon Provider">
                            {settings?.couponProvider && (
                                <Chip
                                    label={
                                        couponProviders.find(
                                            option => option.value === settings?.couponProvider
                                        )?.label
                                    }
                                    color="primary"
                                />
                            )}
                        </SettingDisplay>
                    )}
                    {globalGiftCardProvider === 'TOGGLE' && (
                        <ToggleGiftCardSettingsDisplay settings={settings} locationId={locationId} />
                    )}
                    {globalGiftCardProvider === 'REVEL' && (
                        <RevelGiftCardSettingsDisplay settings={settings} locationId={locationId} />
                    )}
                    {locationId === undefined && (
                        <>
                            <SettingDisplay label="Solo Stamps Enabled">
                                {settings?.physicalStampsEnabled && <Done />}
                                {!settings?.physicalStampsEnabled && <Close />}
                            </SettingDisplay>
                            <SettingDisplay label="SnowShoe API Key">
                                <Typography variant="body2">{settings?.snowShoe?.key}</Typography>
                            </SettingDisplay>
                            <SettingDisplay
                                label="Awards Placeholder"
                                buildtimeAndroid
                                buildtimeIOS
                                description="600px by 400px PNG or JPEG"
                            >
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    flexWrap="wrap"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <img src={awardsPlaceHolderUrl} className={classes.image} />
                                    <IconButton
                                        disabled={awardsPlaceholderLoading}
                                        onClick={handleDownloadAwardsPlaceholder}
                                    >
                                        <Download />
                                    </IconButton>
                                </Box>
                            </SettingDisplay>
                            <SettingDisplay
                                label="External Awards Placeholder"
                                buildtimeAndroid
                                buildtimeIOS
                                description="600px by 400px PNG or JPEG"
                            >
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    flexWrap="wrap"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <img src={externalAwardsPlaceHolderUrl} className={classes.image} />
                                    <IconButton
                                        disabled={externalAwardsPlaceholderLoading}
                                        onClick={handleDownloadExternalAwardsPlaceholder}
                                    >
                                        <Download />
                                    </IconButton>
                                </Box>
                            </SettingDisplay>
                            <SettingDisplay
                                label="Expiry Icon on Award"
                                buildtimeAndroid
                                buildtimeIOS
                                description="50px by 50px PNG or JPEG"
                            >
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    flexWrap="wrap"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <img src={awardExpiryIconUrl} className={classes.image} />
                                    <IconButton
                                        disabled={awardExpiryIconLoading}
                                        onClick={handleDownloadAwardExpiryIcon}
                                    >
                                        <Download />
                                    </IconButton>
                                </Box>
                            </SettingDisplay>
                            <SettingDisplay label="Coupon Provider">
                                {settings?.couponProvider && (
                                    <Chip
                                        label={
                                            couponProviders.find(
                                                option => option.value === settings?.couponProvider
                                            )?.label
                                        }
                                        color="primary"
                                    />
                                )}
                            </SettingDisplay>
                            {settings.couponProvider === 'EAGLE_EYE' && settings?.eagleEye && (
                                <>
                                    <SettingDisplaySwitch
                                        title="Production Environment"
                                        checked={settings?.eagleEye?.isProduction}
                                    />
                                    <SettingDisplay label="Client ID">
                                        <Typography variant="body2">
                                            {settings?.eagleEye?.clientId}
                                        </Typography>
                                    </SettingDisplay>
                                    <SettingDisplay label="Secret">
                                        <Typography variant="body2">{settings?.eagleEye?.secret}</Typography>
                                    </SettingDisplay>
                                </>
                            )}
                            <SettingDisplay
                                label="No Awards Placeholder Image "
                                buildtimeAndroid
                                buildtimeIOS
                                description="600px by 400px PNG or JPEG"
                            >
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    flexWrap="wrap"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <img src={noAwardsPlaceholderUrl} className={classes.image} />
                                    <IconButton
                                        disabled={noAwardsPlaceholderLoading}
                                        onClick={handleDownloadNoAwardsPlaceholder}
                                    >
                                        <Download />
                                    </IconButton>
                                </Box>
                            </SettingDisplay>
                            <SettingDisplay label="Barcode Position on Awards Screen">
                                <Chip
                                    label={
                                        awardsScreenBarcodePositionOptions.find(
                                            option =>
                                                option.value === settings?.app?.awardsScreenBarcodePosition
                                        )?.label ?? 'Unset'
                                    }
                                    color="primary"
                                />
                            </SettingDisplay>
                        </>
                    )}
                </Box>
            }
        />
    );
}
